// Pin img imports for map key.
import onlinePin from './img/map-pin-online-pattern.png';
import offlinePin from './img/map-pin-offline-pattern.png';
import selectedPinOnline from './img/online-selected.png';
import selectedPinOffline from './img/offline-selected.png'; 

// React imports.
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from "react-router-dom";
// Leaftlet (map) imports.
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Icon } from "leaflet";
// Clipboard copy button.
import {CopyToClipboard} from 'react-copy-to-clipboard';
// Custom CSS
import './css/Dashboard.css';
// Bootstrap.
import 'bootstrap/dist/css/bootstrap.min.css';
// React suite for daterange picker and more.
import {DateRangePicker, Message, useToaster, Popover, Whisper, Loader} from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
const { allowedMaxDays, afterToday, combine} = DateRangePicker;


// Global variables.
let hasRun = false;                     // To help identify when the page is initially loaded.
const SERVER_LINK = "https://leoscope.surrey.ac.uk/dashboard";  // Website link - used for the "Copy link" button.
const GRAFANA_LINK = "https://leoscope.surrey.ac.uk/grafana/d-solo/ddrwwa0q90irke/leoscope-dashboard?orgId=1";  // Link to grafana dashboard. Used in grafana pannels.
const getNodesApiLink = "https://leoscope.surrey.ac.uk/api/get-nodes-dash";   // Used to populate map with markers. Was previously 'https://netsys.surrey.ac.uk/upload?tbls=nodes'
let graphNodes = [];                    // Global variable to declare what nodes are to be placed in the graph.
let formattedNodes = [];                // Used  to map the id and name of each node together for use in the SQL query for Grafana.


// Icon pins for nodes on graph.
const onlineNodeIcon = new Icon({
  iconUrl: require("./img/map-pin-online-pattern.png"),
  iconSize:[38,40],
  iconAnchor:[18,30]
})
const offlineNodeIcon = new Icon({
  iconUrl: require("./img/map-pin-offline-pattern.png"),
  iconSize:[38,40],
  iconAnchor:[18,30]
})
const selectedOnlineNodeIcon = new Icon({
  iconUrl: require("./img/online-selected.png"),
  iconSize:[52,55],
  iconAnchor:[26,39]
})
const selectedOfflineNodeIcon = new Icon({
  iconUrl: require("./img/offline-selected.png"),
  iconSize:[52,55],
  iconAnchor:[26,39]
})


const Dashboard = () => {
  // Project constants
  const downloadLink = useRef(null);
  const [searchParams] = useSearchParams();                   // Get URL params.
  const [markers, setMarkers] = useState([]);                 // Map markers.
  const [copied, setCopied] = useState(false);                // Copy link hook.
  const [theme, setTheme] = useState("&theme=light");         // Used to set theme to light/dark inside grafana URLs.
  const [dateRange, setDateRange] = useState(() => {          // Calendar datepicker variable.
    const today = new Date();
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    return [lastWeek, today];
  });             
  const [isScrolled, setIsScrolled] = useState(false);                  // Page scroll button bottom right corner.
  const [disableAddToMap, setDisableAddToMap] = useState(false);        // Enable/ disable map "add to graphs" button.
  const [disableRemoveNode, setDisableRemoveNode] = useState(false);    // Enable/ disable map "remove from graphs" button.
  const [latencyDataLoading, setLatencyDataLoading] = useState(false);  // Loading icons for download buttons.
  const [downloadDataLoading, setDownloadDataLoading] = useState(false);
  const [uploadDataLoading, setUploadDataLoading] = useState(false);
  // const [mapURL, setMapURL] = useState("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"); -- Switch to this map for local languages (eg: Spain -> Español)
  const [mapURL, setMapURL] = useState("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png");
  const node = searchParams.get('node');    // Get URL params for node name, to and from dates in unix time (ms).
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const [iframeSourceLatency, setIframeSourceLatency] = useState(null); // Set iframe source links.
  const [iframeSourceDownload, setIframeSourceDownload] = useState(null);
  const [iframeSourceUpload, setIframeSourceUpload] = useState(null);

  // Set variables related to React Suite
  const noDateMsg = (
    <Message showIcon type={'info'} closable>
      <strong>info!</strong> You must select a date and time range on the picker below. We cannot dynamically download data based on where you've zoomed.
    </Message>
  );
  const toaster = useToaster();
  const popupPlacement = 'topCenter';
  const mapKeySpeaker = (
    <Popover>
      <div id="map-key-popup">
        <p>
          <img src={onlinePin} width="38" height="40" alt="online pin"></img>
          <strong className='text-success'>Green</strong>  -  Node online.<br/>

          <div className='my-2'>
            <img src={offlinePin} width="38" height="40" alt="online pin"></img>
            <strong className='text-danger'>Red</strong>    -  Node offline.<br/>
          </div>

          <img src={selectedPinOnline} width="38" height="40" alt="online pin"></img>
          <img src={selectedPinOffline} width="38" height="40" alt="online pin"></img>
          -  Node selected.<br/>
        </p>
      </div>
    </Popover>
  );
  const controlsSpeaker = (
    <Popover>
      <div id="controls-popup">
        <p>
          <h5 className='text-center'>Map Controls</h5>
          These are controls for selecting different nodes from the map to be displayed simulataneously on the graphs.
          <li><strong>Selecting a node:</strong> Select <mark>"Set to graph"</mark> after clicking on a node. </li>
          <li><strong>Adding multiple nodes to the same graph:</strong> Select <mark>"Add to graph"</mark> after clicking on a node. If you cannot find your node on the map then see URL controls below</li>
          <li><strong>Removing node from graph:</strong> Select <mark>"Remove from graph"</mark> after clicking on a node. If you cannot find the node on the graph then select "Copy link" then remove the desired node from the list of nodes in the URL and paste into another tab.</li>
          <br/>

          <h5 className='text-center'>Graph Controls</h5>
          These are controls for interacting with the graph itself (eg: zooming).<br/>
          <li><strong><u>click + drag</u></strong> Zoom into a particular area.</li>
          <li><strong><u>double click</u></strong> Zoom out.</li>
          <li><strong>Download data/ reset time range:</strong> See buttons in top right corner of each graph panel. To refresh all graphs click <mark>"Syncronise timeframes"</mark>. Please note: if you wish to download data for longer than 7 days please email <a href="mailto:n.sastry@surrey.ac.uk">Dr. Nishanth Sastry</a> (n.sastry@surrey.ac.uk)</li>
          <li><strong><u>shift + click + drag</u></strong> Zoom into particular area (in both x and y directions).</li>
          <br/>
          The area below the graph is called the "legend". By interacting with this you can quickly flick between the nodes you have selected.<br/>
          <li><strong><u>click</u> (on node name)</strong> Only shows selected node.</li>
          <li><strong><u>ctrl+click</u> (on node name)</strong> hides node, can be used on multiple nodes.</li>
          <br/>

          <h5 className='text-center'>URL Controls</h5>
          Add parameters to the URL to automically select nodes and/or a timeframe of your choice. Example URL with parameters: <u>https://leoscope.surrey.ac.uk/dashboard?node=AL+NS&from=1722466800000&to=1725058800000</u>.
          <li><strong>Selecting nodes:</strong> simply use a "+" sybmbol between nodes in the URL, "?node=<b>[node 1 name]+[node 2 name]</b>". e.g. ?node="node1+node2".</li>
          <li><strong>Selecting timeframe:</strong> the timeframe is selected with "from" and "to" parameters which are a UNIX timestamp in milliseconds (UTC). This can be confusing so we recommend you use the calendar feature and select <mark>"copy link"</mark> for selecting the time zone.</li>
          <br/>

          <h5 className='text-center'>Extra Controls</h5>
          These are features in the website to help with displaying your required data.<br/>
          <li><strong>Sharing:</strong> You can share your dashboard by clicking the <mark>"Copy link"</mark> button</li>
          <li><strong>View nodes between certain dates/times:</strong> Click the <mark>"date range"</mark> button to view a certain range of dates. This time range will be applied across all graphs and included if you attempt to share the graph. Defaults to the last 7 days.</li>
          <br/>
          Note: a list of all node names and locations can be found <a href="https://leoscope.surrey.ac.uk/view-nodes">here</a> (you will need to login with your account).
        </p>
      </div>
    </Popover>
  );

  // Get data from nodes table (populate markers hook and fill data mapping of node names to IDs).
  useEffect(() => {
    if (!hasRun) {
      hasRun = true;

      const fetchMarkers = async () => {
        try {
          const response = await fetch(getNodesApiLink, {
            method: 'GET'
          });
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          // Get data into json.
          const data = await response.json();
          // Populate markers hook for leaflet map.
          const formattedMarkers = data.map(node => ({
            online: node.status === "online",
            popUp: node.node_name,
            geocode: [node.longitude,node.latitude]            
          }));
          setMarkers(formattedMarkers);
          // Populate formatted nodes for grafana queries.
          formattedNodes = data.map(node => ({
            id: node.id,
            name: node.node_name,
          }));

          // Find if any nodes are entered in the URL. If so, pass this information to the iframe in a format grafana accepts.
          if (node === null){
            // graphNodes.push(graphNodeInit[0]);

            // If nothing specified in the URL, push all nodes into grafana.
            for (let i=0; i<formattedNodes.length; i++){
              graphNodes.push(formattedNodes[i].name);
            }
          } else {
            // Add each node in url to graphNodes.
            const nodes = node.split(' '); // Split the string at each comma
            for (let i = 0; i < nodes.length; i++) {
              graphNodes.push(nodes[i]);
            }
          }
          setGraphs();

        } catch (error) {
          console.error('Error fetching from API:', error);
        }
      };
      fetchMarkers();

      // Set dateRange before calendar feature is loaded.
      if (from !== null && to !== null) {
        dateRange[0] = new Date(parseInt(from,10));
        dateRange[1] = new Date(parseInt(to,10));
      }
    }
  }, );

  // Pass in a list of nodes, set grafana to be those.
  const setGraphs = () => {
    // React state hooks take time to update, which messes up the fetching of the current URL when adding a daterange -hence normal variables must be used here.
    let tempLatency = GRAFANA_LINK+`&var-latency_injection=${createSqlInjection("latency")}&panelId=2`;
    let tempDownload = GRAFANA_LINK+`&var-download_injection=${createSqlInjection("download")}&panelId=4`;
    let tempUpload = GRAFANA_LINK+`&var-upload_injection=${createSqlInjection("upload")}&panelId=3`;
    
    // If user has selected a datetime range then add this onto the end of the link.
    if (dateRange !== null && dateRange.length !== 0) {
      const timeRangeInURL = `&from=${Math.floor(dateRange[0].getTime())}&to=${Math.floor(dateRange[1].getTime())}`;
      tempLatency += timeRangeInURL;
      tempDownload += timeRangeInURL;
      tempUpload += timeRangeInURL;
    } else if (from !== null && to !== null) {
      const enteredTimeRange = "&from="+from+"&to="+to;
      tempLatency += enteredTimeRange;
      tempDownload += enteredTimeRange;
      tempUpload += enteredTimeRange;
    }
    setIframeSourceLatency(tempLatency);
    setIframeSourceDownload(tempDownload);
    setIframeSourceUpload(tempUpload);
  }

  // Returns SQL to be injected as a variable into grafana (via URL).
  // SECURITY: Whilst "SQL injection" screams alarm bells, Grafana has been set up as a db user that can only perform "SELECT" commands.
  function createSqlInjection(type){
    let sqlInjection = "";
    let nodeid = null;
    let skipNode = false;
    let nodesToRemove = []; // Array of indexes of all nodes that are not in our data (human error in URL).

    // Loop through each node added by the graph and create injection.
    for (let i = 0; i < graphNodes.length; i++) {
      // Find node's ID.
      for (let j = 0; j < formattedNodes.length; j++) {
        if (formattedNodes[j].name === graphNodes[i]) {
          nodeid = formattedNodes[j].id;
          break;
        }
        // If we have exhaused all the nodes that exist then the node cannot be found. This could be due to a user error in the URL or a database error.
        if (j === formattedNodes.length - 1) {
          skipNode = true;
        }
      }

      // Skip the node and reset flag.
      if (skipNode){
        skipNode = false;
        nodesToRemove.push(i);
        continue;
      }

      // Add to injection
      let field = null;
      if (type === "latency") {
        field = "pop_ping_latency_ms";
      } else if (type === "download") {
        field = "down_transfer";
      } else if (type === "upload") {
        field = "up_transfer";
      }
      sqlInjection += "MAX%28CASE+WHEN+node_id%3D"+nodeid+"+THEN+"+field+"+END%29AS%22"+graphNodes[i]+"%22,";
    }
    // Go through and delete nodes to be removed from graphNodes. This must be done in reverse order.
    for (let i = nodesToRemove.length-1; i >= 0; i--){
      graphNodes.splice(nodesToRemove[i], 1);
    }
    // Remove last character (comma).
    return sqlInjection.slice(0, sqlInjection.length-1);
  }

  // Detect scrolled position on webpage
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0); // Check if scrolled past top
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll); // Cleanup
  }, []);

  // Prevent user accidentally refreshing page in case they meant to copy their dashboard link.
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Refresh iframes, which in turn resets the time frame that is selected.
  function resetAllTimeframes() {
    document.getElementById('latency').src = document.getElementById('latency').src;
    document.getElementById('download').src = document.getElementById('download').src;
    document.getElementById('upload').src = document.getElementById('upload').src;
  }

  // Refresh latency graph.
  function resetLatency() {
    document.getElementById('latency').src = document.getElementById('latency').src;
  }

  // Refresh download speed graph.
  function resetDownload() {
    document.getElementById('download').src = document.getElementById('download').src;
  }

  // Refresh upload speed graph.
  function resetUpload() {
    document.getElementById('upload').src = document.getElementById('upload').src;
  }

  // For datetime pickers.
  const handleDateChange = (selectedDateRange) => {
    setDateRange(selectedDateRange); // Assuming single date selection
  };

  // Scrolls to top or bottom of website depending on sroll position.
  const scrollToTop = () => {
    if (isScrolled) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
    }
  }

  // Scrolls to top of website.
  const toggleMap = () => {
    // Get dark theme map button.
    const darkThemeButton = document.getElementById('map-dark-theme-btn');
    const bodyElement = document.querySelector('.Dashboard'); 
    const mapKeyButton = document.getElementById('map-key');
    // If dark theme set to light.
    if (mapURL === "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"){
      setMapURL("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png");
      darkThemeButton.className = 'btn btn-dark';
      mapKeyButton.className = 'btn btn-outline-dark';
      // bodyElement.style.backgroundColor = "#FFFFFF";
      setTheme("&theme=light");
    } else {
      setMapURL("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png");
      darkThemeButton.className = 'btn btn-light';
      mapKeyButton.className = 'btn btn-outline-light';
      // bodyElement.style.backgroundColor = "#1a1a1a";
      setTheme("&theme=dark");
    }
  }

  // Set graphNodes to only contain node passed in.
  const setToGraph = (node) => {
    graphNodes = [];
    graphNodes.push(node);
    setGraphs();
    setDisableRemoveNode(true);
    setDisableAddToMap(true);
  }

  // Add node passed in to graphNodes.
  const addToGraph = (node) => {
    // If number of nodes on the graphs are 0 then add this button should act like the "Set graph" button.
    if (graphNodes.length === 0){
      setToGraph(node);
    }
    // Check that node hasn't already been added.
    if (!graphNodes.includes(node)){
      graphNodes.push(node);
      setGraphs();
      if (disableRemoveNode){
        setDisableRemoveNode(false);
      }
      setDisableAddToMap(true);
    }
  }

  // Remove node passed in from graphNodes.
  const removeFromGraph = (node) => {
    // Do nothing if nothing is in the list of graphs.
    if (graphNodes.length === 0){
      return;
    } 
    // Check that node hasn't already been added and that there is more than one node on the graph.
    if (graphNodes.includes(node) && graphNodes.length > 1){
      // Otherwise remove node from graphNodes and set graphs.
      const index = graphNodes.indexOf(node);
      graphNodes.splice(index, 1);
      setGraphs();
      // Show "Remove from graph" button as disabled.
      setDisableRemoveNode(true);
      setDisableAddToMap(false);
    }
  }

  // Map marker onClick event.
  const eventHandlers = {
    click: (e) => {
      // Pass in the node name via "e".
      checkIfNodeShown(e.target.options.node_name);
      // Ensure "Remove from graph" button is disabled if only 1 node is in the graph.
      if (graphNodes.length === 1){
        setDisableRemoveNode(true);
      }
    }
  };

  // Check if node passed in is included in the shown graphs. Disable/enable corresponding buttons.
  const checkIfNodeShown = (node) => {
    if (graphNodes.includes(node)){
      setDisableRemoveNode(false);
      setDisableAddToMap(true);
    } else {
      setDisableRemoveNode(true);
      setDisableAddToMap(false);
    }
  }

  // Set button to "Link copied!" then back to "Copy link" after 1 second.
  const copyLink = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1000); 
  }

  // Function to return website URL for current dashboard in "Copy Link" button.
  function sendLink() {
    let link = SERVER_LINK;
    // Check if any nodes added to graph. At least 1 node will always be selected.
    if (graphNodes.length > 0) {
      // Get browser link corresponding to current nodes displayed in graphs.
      link += "?node=" + graphNodes.join("+");
    }
    // Check if user has selected a date time range.
    if (dateRange !== null && dateRange.length !== 0) {
      link += `&from=${Math.floor(dateRange[0].getTime())}&to=${Math.floor(dateRange[1].getTime())}`;
    }

    return link;
  }

  const downloadDataMessages = (dataType) => {
    // Check if user has selected timerange. Skip download and show a msg if not.
    if (dateRange === null || dateRange.length === 0) {
      toaster.push(noDateMsg, { popupPlacement, duration: 6000 })
      return;
    }

    // Grab dates from calendar to format in yyyy-mm-dd. This must be in this format for later use in the api query.
    const formattedStartDate = dateRange[0].toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const formattedEndDate = dateRange[1].toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });

    // Reverse date format from yyyy-mm-dd to dd-mm-yyyy.
    const reversedStartDate = formattedStartDate.split('-').reverse().join('-');
    const reversedEndDate = formattedEndDate.split('-').reverse().join('-');

    // Show a message to check the dates selected are okay.
    toaster.push(
      <Message showIcon type={'info'} closable>
        <strong>info!</strong> You are about to download <strong>{dataType} {dataType==="download"||dataType==="upload" ? "speed" : ""}</strong> data for node{graphNodes.length>1 ? "s":""}: <strong>{graphNodes.join(', ')}</strong> from <strong>{reversedStartDate}</strong> to <strong>{reversedEndDate}</strong>.
        <br/>
        <div className='d-flex justify-content-center pt-1'>
          <button className="btn btn-outline-primary" onClick={() => {downloadData(); toaster.clear();}} style={{ borderRadius: '10px 0 0 10px' }}>Download</button>
          <button className="btn btn-outline-danger" onClick={() => toaster.clear()} style={{ borderRadius: '0 10px 10px 0' }}>Cancel</button>
        </div>
      </Message>,
      { popupPlacement, duration: 0 }
    )

    // Function to actually download data after user confirms.
    const downloadData = async () => {

      let tableName = "";
      if (dataType === "latency"){
        setLatencyDataLoading(true);
        tableName = "grpc";
      } else if (dataType === "download"){
        setDownloadDataLoading(true);
        tableName = "iperf_downlink";
      } else if (dataType === "upload"){
        setUploadDataLoading(true);
        tableName = "iperf_uplink";
      }
      const fileName = dataType + "_" + formattedStartDate + "_to_" + formattedEndDate; 

      // Find corresponding formatted node for each selected (graph) node to find id to append to download link..
      let idsToAppend = "";
      graphNodes.forEach(graphNode => {
        const formattedNode = formattedNodes.find(node => node.name === graphNode);
        if (formattedNode) {
          idsToAppend += "&node_id=" + formattedNode.id;
        }
      });

      try {
        const response = await fetch(`https://netsys.surrey.ac.uk/upload?tbls=${tableName}&sd=${formattedStartDate}&ed=${formattedEndDate}${idsToAppend}`);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        downloadLink.current.href = url;
        downloadLink.current.download = fileName;
        downloadLink.current.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading data:', error);
      } finally {
        setLatencyDataLoading(false);
        setDownloadDataLoading(false);
        setUploadDataLoading(false);
      }
    }
  }

  return (
    <div className='Dashboard'>
      <h1 className='my-4'>LEOScope Dashboard</h1>

      {/* Map */}
      <div className="map-container">
        {/* Map key button */}
        <Whisper
          placement="bottom"
          trigger="click"
          controlId="control-id-hover-enterable"
          speaker={mapKeySpeaker}
          enterable>
          <button id='map-key' className='btn btn-outline-dark d-inline-flex align-items-center info'>
            <svg id="map-key-btn" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-geo-alt-fill" viewBox="0 0 16 16">
              <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6"/>
            </svg>
            <b>Map key</b>
          </button>
        </Whisper>

        {/* Toggle dark theme button */}
        <button onClick={toggleMap} id="map-dark-theme-btn" className="btn btn-dark">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-moon-stars-fill" viewBox="0 0 16 16">
            <path d="M6 .278a.77.77 0 0 1 .08.858 7.2 7.2 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277q.792-.001 1.533-.16a.79.79 0 0 1 .81.316.73.73 0 0 1-.031.893A8.35 8.35 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.75.75 0 0 1 6 .278"/>
            <path d="M10.794 3.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387a1.73 1.73 0 0 0-1.097 1.097l-.387 1.162a.217.217 0 0 1-.412 0l-.387-1.162A1.73 1.73 0 0 0 9.31 6.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387a1.73 1.73 0 0 0 1.097-1.097zM13.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.16 1.16 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.16 1.16 0 0 0-.732-.732l-.774-.258a.145.145 0 0 1 0-.274l.774-.258c.346-.115.617-.386.732-.732z"/>
          </svg>
        </button>

        <MapContainer center={[54.386213, -2.221549]} zoom={1} minZoom={1} maxBoundsViscosity={1} language="en">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url={mapURL}
          />
          {markers.map(marker =>(
            marker.geocode && marker.geocode[0] !== null && marker.geocode[1] !== null ? (
              <Marker node_name={marker.popUp} position={marker.geocode} 
              icon={graphNodes.includes(marker.popUp)
                ? (marker.online? selectedOnlineNodeIcon : selectedOfflineNodeIcon)
                : (marker.online ? onlineNodeIcon : offlineNodeIcon)} 
              eventHandlers={eventHandlers}>

                <Popup id="map-popup">
                  <div className="text-center">
                    <div className="pb-2">
                      <b className="marker-popup">{marker.popUp}</b>
                    </div>
                    <div className="popup-button-div">
                      <button className="btn btn-primary d-inline-flex align-items-center popup-button" onClick={() => setToGraph(marker.popUp)}>Set to graph</button>
                    </div>
                    <div className="popup-button-div">
                      <button className="btn btn-secondary d-inline-flex align-items-center popup-button" disabled={disableAddToMap} onClick={() => addToGraph(marker.popUp)}>Add to graph</button>
                    </div>
                    <div>
                      <button className="btn btn-danger d-inline-flex align-items-center popup-button" disabled={disableRemoveNode} onClick={() => removeFromGraph(marker.popUp)}>Remove from graph</button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ) : null  // Don't render the marker if either the lat or long of geocode is null.
          ))}
        </MapContainer>
      </div>

      {/* Stats */}
      <div className="stats-container mt-1">
        <div class="m-1 mt-2 stat-block">
          <iframe class="stats" src={GRAFANA_LINK+"&panelId=5"+theme} title="stat_download"></iframe>
        </div>
        <div class="m-1 stat-block" id="upload-stat">
          <iframe class="stats" src={GRAFANA_LINK+"&panelId=7"+theme} title="stat_upload"></iframe>
        </div>
        <div class="m-1 stat-block" id="latency-stat">
          <iframe class="stats" src={GRAFANA_LINK+"&panelId=6"+theme} title="stat_latency"></iframe>
        </div>
      </div>

      {/* Graphs */}
      <div className="graphs-container p-2">
        <div className='sub-graph-container'>

          {/* Download */}
          <button className="btn btn-secondary d-inline-flex align-items-center download" onClick={() => downloadDataMessages("download")}>
            {downloadDataLoading ? 
              <Loader/>
              :
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-download" viewBox="0 0 16 16">
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/>
              </svg>
            }
          </button>

          {/* Refresh */}
          <button className="btn btn-secondary d-inline-flex align-items-center reset" onClick={resetDownload}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-clockwise" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>
            </svg>
          </button>

          {/* Graph */}
          <iframe id="download" className="graphs mt-1" title="graph_download" src={iframeSourceDownload+theme}></iframe>
        </div>

        <div className='sub-graph-container'>

          {/* Download */}
          <button className="btn btn-secondary d-inline-flex align-items-center download" onClick={() => downloadDataMessages("upload")}>
            {uploadDataLoading ? 
              <Loader/>
              :
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-download" viewBox="0 0 16 16">
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/>
              </svg>
            }
          </button>

          {/* Refresh */}
          <button className="btn btn-secondary d-inline-flex align-items-center reset" onClick={resetUpload}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-clockwise" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>
            </svg>
          </button>

          {/* Graph */}
          <iframe id="upload" className="graphs mt-1" title="graph_upload" src={iframeSourceUpload+theme}></iframe>
        </div>

        <div className='sub-graph-container'>

          {/* Download */}
          <button className="btn btn-secondary d-inline-flex align-items-center download" onClick={() => downloadDataMessages("latency")}>
            {latencyDataLoading ? 
              <Loader/>
              :
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-download" viewBox="0 0 16 16">
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/>
              </svg>
            }
          </button>

          {/* Refresh */}
          <button className="btn btn-secondary d-inline-flex align-items-center reset" onClick={resetLatency}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-clockwise" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>
            </svg>
          </button>

          {/* Graph */}
          <iframe id="latency" className="graphs mt-1" title="graph_latency" src={iframeSourceLatency+theme}></iframe>
        </div>
      </div>

      {/* Buttons to reset timeframes or copy the dashboard link */}
      <div className="p-3">
        <div>
          {/* Selector to update timeframe of all graphs simulataneously */}
          <DateRangePicker 
            onChange={handleDateChange}
            value={dateRange}
            placeholder="Select Date Range"
            format="dd/MM/yyyy" 
            showMeridian
            size="lg"
            shouldDisableDate={combine(allowedMaxDays(7),afterToday())}
            onExiting={setGraphs}
            placement="topStart"
            showOneCalendar
          />

          {/* Button to refresh graphs to original time frame */}
          <button className="btn btn-secondary d-inline-flex align-items-center reset-and-copy-link" style={{ margin: '10px' }} onClick={resetAllTimeframes}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" id="reset-all-timeframes" className="bi bi-arrow-clockwise" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>
            </svg>
            Syncronise timeframes
          </button>

          {/* Button to copy link of dashboard with current graphs */}
          <CopyToClipboard text={sendLink()} onCopy={copyLink}>
            <button id="copy-link" className="btn btn-outline-secondary d-inline-flex align-items-center reset-and-copy-link">
              {copied ? "Link copied!" : (<>  
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-share-fill" viewBox="0 0 16 16">
                  <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5"/>
                </svg>
              Copy link</>)}
            </button>
          </CopyToClipboard>

          {/* Scrolls to top or bottom of page */}
          <button id="scoll-to-top-btn" className="btn btn-secondary d-inline-flex align-items-center rounded-circle" onClick={scrollToTop}>
            {isScrolled ?
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-caret-up-fill" viewBox="0 0 16 16">
                <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z"/>
              </svg>
              :
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-caret-down-fill" viewBox="0 0 16 16">
                <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
              </svg>
            }
          </button>
        </div>

        <div className='my-1'>
          {/* Help button */}
          <Whisper
          placement="top"
          trigger="click"
          controlId="control-id-hover-enterable"
          speaker={controlsSpeaker}
          enterable>
            <button className='btn btn-secondary d-inline-flex align-items-center info'>
              <svg id="info-btn" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-info-circle" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
              </svg>
              Controls
            </button>
          </Whisper>
        </div>

        
      </div>
      
      <br/>
      {/* This is needed for the download buttons to work. */}
      <a ref={downloadLink} style={{ display: 'none' }}>Download</a>
    </div>
  )
};

export default Dashboard;
